import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import "../assets/css/pages/PageTransition.css";

const PageTransition = ({ children }) => {
    const location = useLocation();
    const overlayRef = useRef(null);

    const [displayChildren, setDisplayChildren] = useState(children);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [childrenReady, setChildrenReady] = useState(false);

    // ✅ Track only PATHNAME changes
    const lastPathRef = useRef(location.pathname);
    const timeoutRef = useRef(null);

    const originalBodyTransformRef = useRef(null);
    const savedPositioningRef = useRef(null);

    /* ---------------- CLEANUP ---------------- */

    const cleanupTransition = () => {
        setIsTransitioning(false);

        document.body.classList.remove("page-transitioning");
        document.documentElement.classList.remove("page-transitioning");

        document.body.style.paddingRight = "";
        document.documentElement.style.paddingRight = "";

        if (savedPositioningRef.current) {
            const s = savedPositioningRef.current;
            document.body.style.position = s.bodyPosition;
            document.body.style.top = s.bodyTop;
            document.body.style.left = s.bodyLeft;
            document.documentElement.style.position = s.htmlPosition;
            savedPositioningRef.current = null;
        } else {
            document.body.style.overflow = "";
            document.body.style.position = "";
            document.documentElement.style.overflow = "";
            document.documentElement.style.position = "";
        }

        if (originalBodyTransformRef.current) {
            document.body.style.transform = originalBodyTransformRef.current;
            originalBodyTransformRef.current = null;
        }

        if (overlayRef.current) {
            overlayRef.current.classList.remove("pt-in", "pt-out");
        }
    };

    /* ---------------- CHILD READY ---------------- */

    useEffect(() => {
        if (!isTransitioning) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setChildrenReady(true);
            });
        });
    }, [displayChildren, isTransitioning]);

    /* ---------------- MAIN TRANSITION ---------------- */

    useEffect(() => {
        if (isTransitioning) return;

        // ✅ FIX: ignore same-path updates (filters, pagination, re-renders)
        if (lastPathRef.current === location.pathname) return;
        lastPathRef.current = location.pathname;

        const overlay = overlayRef.current;
        if (!overlay) return;

        setIsTransitioning(true);
        setChildrenReady(false);

        const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;

        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
        }

        const bodyStyles = getComputedStyle(document.body);
        const htmlStyles = getComputedStyle(document.documentElement);

        if (bodyStyles.position === "fixed" || htmlStyles.position === "fixed") {
            savedPositioningRef.current = {
                bodyPosition: document.body.style.position,
                bodyTop: document.body.style.top,
                bodyLeft: document.body.style.left,
                htmlPosition: document.documentElement.style.position,
            };

            document.body.style.position = "static";
            document.body.style.top = "0";
            document.body.style.left = "0";
            document.documentElement.style.position = "static";
        }

        const bodyTransform = bodyStyles.transform;
        if (bodyTransform && bodyTransform !== "none") {
            originalBodyTransformRef.current = bodyTransform;
            document.body.style.transform = "none";
        }

        document.body.classList.add("page-transitioning");
        document.documentElement.classList.add("page-transitioning");

        timeoutRef.current = setTimeout(() => {
            cleanupTransition();
        }, 900);

        overlay.style.visibility = "visible";
        overlay.classList.remove("pt-out");
        overlay.classList.add("pt-in");

        const handleInEnd = (e) => {
            if (e.target !== overlay || e.propertyName !== "transform") return;

            overlay.removeEventListener("transitionend", handleInEnd);

            setDisplayChildren(children);
            window.scrollTo(0, 0);

            const startOut = () => {
                overlay.classList.remove("pt-in");
                overlay.classList.add("pt-out");

                overlay.addEventListener(
                    "transitionend",
                    (ev) => {
                        if (ev.target !== overlay || ev.propertyName !== "transform") return;

                        clearTimeout(timeoutRef.current);
                        cleanupTransition();
                    },
                    { once: true }
                );
            };

            if (childrenReady) {
                startOut();
            } else {
                const start = Date.now();
                const i = setInterval(() => {
                    if (childrenReady || Date.now() - start > 500) {
                        clearInterval(i);
                        startOut();
                    }
                }, 16);
            }
        };

        overlay.addEventListener("transitionend", handleInEnd);

        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, [location.pathname, children, childrenReady, isTransitioning]);

    /* ---------------- RENDER ---------------- */

    return (
        <>
            <div className="pt-content">{displayChildren}</div>
            <div
                ref={overlayRef}
                className="pt-overlay"
                style={{
                    visibility: "visible",
                    pointerEvents: "none",
                }}
            />
        </>
    );
};

export default PageTransition;


